// @ts-check
// Rotas REST dos bots (contrato de bots, seção 19). Todas exigem o módulo Atendimento e a flag SERVICE_DESK_SETTINGS,
// menos as da conversa (seção 19.2, quem vê a conversa). No modo demonstração, client/src/data/mock-bots.js responde no
// mesmo formato.
import { api } from './api-client.js';

/**
 * @typedef {import('../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../lib/bot-validation.js').BotEditorProblem} BotProblem
 * @typedef {import('../lib/bot-list.js').BotSummary} BotSummary
 * @typedef {import('../lib/bot-stats.js').BotStats} BotStats
 * @typedef {{ id: string, name: string, description: string | null, purpose: 'CAPTURE' | 'SERVICE' | 'ANY', status: 'DRAFT' | 'ACTIVE' | 'INACTIVE',
 *   inactiveReason: string | null, inactiveDetails: Record<string, unknown> | null, priority: number, revision: number, definition: BotDefinition,
 *   owner: { id: string, name: string } | null, templateKey: string | null, problems: BotProblem[], hasRuns: boolean, createdAt: string,
 *   updatedAt: string, activatedAt: string | null }} BotDetail
 * @typedef {{ key: string, kind: 'CHANNEL' | 'TEAM' | 'BOT' | 'MEMBERS' | 'BOOKING_PAGE', labelKey: string, teamKey?: string, botTemplateKey?: string }} BotTemplateChoice
 * @typedef {{ key: string, nameKey: string, descriptionKey: string, category: 'CAPTURE' | 'SERVICE' | 'RELATIONSHIP', icon: string,
 *   purpose: 'CAPTURE' | 'SERVICE' | 'ANY', choices: BotTemplateChoice[], definition: BotDefinition }} BotTemplateItem
 * @typedef {{ botId: string, botName: string, triggerKind: string, channelId: string }} BotTriggerConflict
 * @typedef {{ code: 'WELCOME_AUTOMATION_ACTIVE' | 'KEYWORD_OVERLAP' | 'NO_TEAM_MEMBERS' | 'CHANNEL_PURPOSE_CAPTURE', details?: Record<string, any> }} BotActivationWarning
 * @typedef {{ problems: BotProblem[], conflicts: BotTriggerConflict[], warnings: BotActivationWarning[],
 *   channels: Array<{ id: string, label: string, purpose: string | null }> }} BotActivationPreview
 * @typedef {{ computedAt: string, nodes: Record<string, { now: number }> }} BotNodeCounts
 * @typedef {{ id: string, status: string, endReason: string | null, currentNodeId: string | null, threadId: string | null, contactName: string | null,
 *   startedAt: string, endedAt: string | null, stepCount: number }} BotRunSummary
 * @typedef {{ nodeId: string, nodeType: string, branchKey: string | null, status: string, reason: string | null, fieldsWritten: string[] | null, at: string }} BotRunStep
 * @typedef {{ id: string, botId: string, status: string, endReason: string | null, currentNodeId: string | null, threadId: string | null,
 *   startedAt: string, endedAt: string | null, stepCount: number, triggerKind?: string | null }} BotRun
 * @typedef {{ revision: number, savedAt: string, savedBy: { id: string, name: string } | null, summary: Record<string, unknown> }} BotRevision
 * @typedef {{ id: string, name: string, eventType: string, durationMinutes: number, isActive: boolean }} BookingPageChoice
 */

/** @param {any} body */
const dataOf = (body) => body?.data ?? {};
/** @param {string} botId */
const botPath = (botId) => `/rest/bots/${encodeURIComponent(botId)}`;

/**
 * @param {{ q?: string | null, status?: string | null, purpose?: string | null, channelId?: string | null }} [query]
 * @returns {Promise<BotSummary[]>}
 */
export async function listBots(query = {}) {
  return dataOf(await api.get('/rest/bots', /** @type {Record<string, string>} */ (Object.fromEntries(Object.entries(query).filter(([, value]) => value))))).bots ?? [];
}

/**
 * Bot novo: em branco, pela galeria (com as escolhas de número e departamentos) ou cópia de outro.
 * @param {{ name: string, purpose?: string, description?: string | null, templateKey?: string, choices?: { channelIds?: string[], teamIds?: Record<string, string>,
 *   botIds?: Record<string, string>, memberIds?: Record<string, string[]>, bookingPageIds?: Record<string, string> },
 *   duplicateOf?: string }} input
 * @returns {Promise<BotDetail>}
 */
export async function createBot(input) {
  return dataOf(await api.post('/rest/bots', input)).bot;
}

/** @returns {Promise<BotTemplateItem[]>} */
export async function listBotTemplates() {
  return dataOf(await api.get('/rest/bots/templates')).templates ?? [];
}

/** @param {string} botId @returns {Promise<BotDetail>} */
export async function getBot(botId) {
  return dataOf(await api.get(botPath(botId))).bot;
}

/**
 * Grava a definição; outra pessoa salvou antes: 409 EDIT_CONFLICT; bot ativo com pendência: 422 BOT_INVALID.
 * @param {string} botId @param {{ definition: BotDefinition, expectedRevision: number }} input
 * @returns {Promise<BotDetail>}
 */
export async function saveBotDefinition(botId, input) {
  return dataOf(await api.put(botPath(botId), input)).bot;
}

/** @param {string} botId @param {{ name?: string, description?: string | null, priority?: number }} patch @returns {Promise<BotDetail>} */
export async function updateBotDetails(botId, patch) {
  return dataOf(await api.patch(botPath(botId), patch)).bot;
}

/** @param {string} botId @returns {Promise<string>} */
export async function deleteBot(botId) {
  return dataOf(await api.delete(botPath(botId))).id;
}

/** @param {string} botId @param {{ expectedRevision: number }} input @returns {Promise<BotActivationPreview>} */
export async function previewBotActivation(botId, input) {
  return dataOf(await api.post(`${botPath(botId)}/activation-preview`, input));
}

/** @param {string} botId @param {{ expectedRevision: number }} input @returns {Promise<BotDetail>} */
export async function activateBot(botId, input) {
  return dataOf(await api.post(`${botPath(botId)}/activate`, input)).bot;
}

/** @param {string} botId @returns {Promise<{ bot: BotDetail, stoppedRuns: number }>} */
export async function deactivateBot(botId) {
  const data = dataOf(await api.post(`${botPath(botId)}/deactivate`, {}));
  return { bot: data.bot, stoppedRuns: Number(data.stoppedRuns ?? 0) };
}

/** @param {string} botId @param {{ name?: string }} [input] @returns {Promise<BotDetail>} */
export async function duplicateBot(botId, input = {}) {
  return dataOf(await api.post(`${botPath(botId)}/duplicate`, input)).bot;
}

/** @param {string} botId @returns {Promise<BotRevision[]>} */
export async function listBotRevisions(botId) {
  return dataOf(await api.get(`${botPath(botId)}/revisions`)).revisions ?? [];
}

/** @param {string} botId @param {{ from: string, to: string, groupBy?: string }} query @returns {Promise<BotStats>} */
export async function getBotStats(botId, query) {
  return dataOf(await api.get(`${botPath(botId)}/stats`, { from: query.from, to: query.to, groupBy: query.groupBy ?? 'day' }));
}

/** @param {string} botId @returns {Promise<BotNodeCounts>} */
export async function getBotNodeCounts(botId) {
  return dataOf(await api.get(`${botPath(botId)}/node-counts`));
}

/**
 * @param {string} botId @param {{ status?: string | null, nodeId?: string | null, limit?: number, startingAfter?: string | null }} [query]
 * @returns {Promise<{ runs: BotRunSummary[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listBotRuns(botId, query = {}) {
  /** @type {Record<string, string>} */
  const parameters = {};
  if (query.status) parameters.status = query.status;
  if (query.nodeId) parameters.nodeId = query.nodeId;
  if (query.limit) parameters.limit = String(query.limit);
  if (query.startingAfter) parameters.startingAfter = query.startingAfter;
  const data = dataOf(await api.get(`${botPath(botId)}/runs`, parameters));
  return { runs: data.runs ?? [], pageInfo: data.pageInfo ?? { hasNextPage: false, endCursor: null } };
}

/** @param {string} runId @returns {Promise<{ run: BotRun, steps: BotRunStep[] }>} */
export async function getBotRun(runId) {
  const data = dataOf(await api.get(`/rest/bots/runs/${encodeURIComponent(runId)}`));
  return { run: data.run, steps: data.steps ?? [] };
}

/**
 * Bots manuais da conversa e a execução aberta (seção 19.2).
 * @param {string} threadId @returns {Promise<{ bots: Array<{ id: string, name: string }>, run: BotRun | null }>}
 */
export async function listConversationBots(threadId) {
  const data = dataOf(await api.get(`/rest/conversations/${encodeURIComponent(threadId)}/bots`));
  return { bots: data.bots ?? [], run: data.run ?? null };
}

/** @param {string} threadId @param {string} botId @returns {Promise<BotRun>} */
export async function startConversationBot(threadId, botId) {
  return dataOf(await api.post(`/rest/conversations/${encodeURIComponent(threadId)}/bot/start`, { botId })).run;
}

/** "Assumir conversa": para o bot e a conversa fica com a equipe. @param {string} threadId */
export async function stopConversationBot(threadId) {
  return dataOf(await api.post(`/rest/conversations/${encodeURIComponent(threadId)}/bot/stop`, {})).thread;
}

/**
 * Páginas de agendamento para o bloco "Agendar visita" (Agendamento integrado, frente F6): só id, nome, tipo, duração e
 * situação; o bloco guarda o id e os horários vêm da página na hora da conversa.
 * @returns {Promise<BookingPageChoice[]>}
 */
export async function listBookingPages() {
  return dataOf(await api.get('/rest/booking-pages')).bookingPages ?? [];
}
